import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Stack, Alert, CircularProgress, Button, Tooltip,
  Divider, List, ListItem, ListItemText,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../api";
import dayjs from "dayjs";

// Дериває статус кожного візиту (майбутній/завершений/скасований/неявка) та зводить статистику.
function analyzeHistory(history) {
  const now = dayjs();
  const items = (history || []).map((h) => {
    let st = h.status;
    if (st !== "cancelled" && st !== "no_show") {
      const start = dayjs(`${String(h.date).slice(0, 10)}T${String(h.time).slice(0, 5)}`);
      st = start.isAfter(now) ? "upcoming" : "completed";
    }
    return { ...h, st };
  });
  const stats = { total: items.length, completed: 0, upcoming: 0, cancelled: 0, no_show: 0 };
  items.forEach((h) => { if (stats[h.st] !== undefined) stats[h.st] += 1; });
  return { items, stats };
}
const ST_COLOR = { completed: "success", upcoming: "info", cancelled: "error", no_show: "warning" };
const ST_KEY = { completed: "statusCompleted", upcoming: "statusUpcoming", cancelled: "statusCancelled", no_show: "statusNoShow" };
const REASON_KEY = { master: "reasonMaster", client: "reasonClient", no_show: "reasonNoShow", other: "reasonOther" };

const STATUS_META = (t) => ({
  white: { label: t("clients.statusWhite"), color: "success" },
  gray: { label: t("clients.statusGray"), color: "warning" },
  black: { label: t("clients.statusBlack"), color: "error" },
});

const fmtDate = (s) => (s ? String(s).slice(0, 10) : "—");

export default function Clients() {
  const { t } = useTranslation();
  const { activeWorkspaceId } = useAuth();
  const statusMeta = STATUS_META(t);

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null); // { client, history }
  const [form, setForm] = useState({ status: "white", notes: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    setError("");
    try {
      const res = await api.getWorkspaceClients(activeWorkspaceId);
      setClients(res.clients || []);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [activeWorkspaceId]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (userId) => {
    setDetail(null);
    setOpen(true);
    try {
      const res = await api.getWorkspaceClient(activeWorkspaceId, userId);
      setDetail(res);
      setForm({
        status: res.client?.status || "white",
        notes: res.client?.notes || "",
      });
    } catch (e) {
      setError(e.message);
      setOpen(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.updateWorkspaceClient(activeWorkspaceId, detail.client.userId, {
        status: form.status,
        notes: form.notes,
      });
      setOpen(false);
      await load();
    } catch (e) {
      setError(e.message);
    }
    setSaving(false);
  };

  if (!activeWorkspaceId) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography color="text.secondary">{t("clients.noWorkspace")}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {t("clients.title")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : clients.length === 0 ? (
        <Typography color="text.secondary">{t("clients.empty")}</Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("clients.name")}</TableCell>
                <TableCell>{t("clients.phone")}</TableCell>
                <TableCell>{t("clients.status")}</TableCell>
                <TableCell align="center">{t("clients.visits")}</TableCell>
                <TableCell>{t("clients.lastSeen")}</TableCell>
                <TableCell align="right">{t("clients.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clients.map((c) => (
                <TableRow key={c.userId} hover>
                  <TableCell>{c.name}</TableCell>
                  <TableCell>{c.phone}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={statusMeta[c.status]?.label || c.status}
                      color={statusMeta[c.status]?.color || "default"}
                      variant={c.status === "white" ? "outlined" : "filled"}
                    />
                  </TableCell>
                  <TableCell align="center">{c.visitCount}</TableCell>
                  <TableCell>{fmtDate(c.lastSeen)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title={t("clients.open")}>
                      <IconButton size="small" onClick={() => openDetail(c.userId)}>
                        <VisibilityOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Деталі клієнта: статус, нотатки, історія в цьому салоні */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {detail?.client?.name || t("clients.title")}
        </DialogTitle>
        <DialogContent dividers>
          {!detail ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                {detail.client.phone}
                {detail.client.email ? ` · ${detail.client.email}` : ""}
              </Typography>
              <TextField
                select
                label={t("clients.status")}
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                fullWidth
              >
                <MenuItem value="white">{statusMeta.white.label}</MenuItem>
                <MenuItem value="gray">{statusMeta.gray.label}</MenuItem>
                <MenuItem value="black">{statusMeta.black.label}</MenuItem>
              </TextField>
              <TextField
                label={t("clients.notes")}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                fullWidth
                multiline
                minRows={2}
                placeholder={t("clients.notesPlaceholder")}
              />
              <Divider textAlign="left">
                <Typography variant="caption" color="text.secondary">
                  {t("clients.history")} ({detail.history.length})
                </Typography>
              </Divider>
              {(() => {
                const { items, stats } = analyzeHistory(detail.history);
                if (items.length === 0) {
                  return <Typography variant="body2" color="text.secondary">{t("clients.noHistory")}</Typography>;
                }
                return (
                  <>
                    {/* Зведена статистика — зокрема скасовані та неявки */}
                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                      <Chip size="small" variant="outlined" label={`${t("bookingDetails.statTotal")}: ${stats.total}`} />
                      {stats.completed > 0 && <Chip size="small" color="success" label={`${t("bookingDetails.statCompleted")}: ${stats.completed}`} />}
                      {stats.upcoming > 0 && <Chip size="small" color="info" label={`${t("bookingDetails.statUpcoming")}: ${stats.upcoming}`} />}
                      {stats.cancelled > 0 && <Chip size="small" color="error" label={`${t("bookingDetails.statCancelled")}: ${stats.cancelled}`} />}
                      {stats.no_show > 0 && <Chip size="small" color="warning" label={`${t("bookingDetails.statNoShow")}: ${stats.no_show}`} />}
                    </Stack>
                    <List dense disablePadding>
                      {items.map((h) => (
                        <ListItem key={h.id} disableGutters
                          secondaryAction={<Chip size="small" color={ST_COLOR[h.st] || "default"} label={t(`bookingDetails.${ST_KEY[h.st]}`)} />}
                        >
                          <ListItemText
                            primary={`${fmtDate(h.date)} · ${String(h.time).slice(0, 5)}`}
                            secondary={
                              (h.masterName || "") +
                              (h.st === "cancelled" && REASON_KEY[h.reason]
                                ? ` · ${t("bookingDetails.reasonLabel")}: ${t(`bookingDetails.${REASON_KEY[h.reason]}`)}`
                                : "")
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </>
                );
              })()}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t("clients.cancel")}</Button>
          <Button variant="contained" onClick={save} disabled={saving || !detail}>
            {t("clients.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
